<template lang="pug">
.user-team-body
  .user-team-body__limiter.-s-(
    v-if="isLoading"
  )
    SkeletonRow(
      v-for="item in 3"
      :key="item"
      :colCount="1"
    )

  .user-team-body__scroll(
    v-else-if="!showPlug"
  )
    .user-team-body__limiter(
      :class="{'-s-': !canEdit}"
    )
      template(v-if="canEdit && mode === 'unallocated'")
        UnallocatedTable(
          :canChangeTeam="true"
          :userList="unallocated"
        )
        template(v-if="isLoadingMoreUnallocated || isUnallocatedLoading")
          SkeletonRow(v-for="item in 2" :key="item" :col-count="5")
        .intersection-observer(ref="unallocatedObserver")
      template(v-if="loadedGroup && mode === 'allocated'")
        Team(
          v-for="(team, index) in groups" :key="team.id"
          :team="team"
          :auto-open="index === 0 && !user.isAdmin && groups.length <= 3"
          :folderId="team.folderId"
        )
        template(v-if="isLoadingMoreGroups || isGroupLoading")
          SkeletonRow(v-for="item in 2" :key="item" :col-count="1")
        .intersection-observer(ref="groupsObserver")
      template(v-else)
        Dropdown(
          v-if="peopleNearby.length"
          title="Участники по городу"
          sizeTitle="xl"
        )
          UserTeam(
            :userList="peopleNearby"
          )

  Plug(
    v-if="showPlug"
    :title="plugText.title"
    :subtitle="plugText.subtitle"
  )

</template>

<script>
import Team from '../Team';
import UnallocatedTable from '../Team/UnallocatedTable';
import Dropdown from './components/Dropdown';
import SkeletonRow from './components/SkeletonRow';
import UserTeam from '../Team/UserTeam';
import Plug from './components/Plug';

import { mapState, mapActions } from 'vuex';
import replaceMultipleRouteQuery from '@/functions/replaceMultipleRouteQuery';

export default {
  components: {
    Dropdown,
    SkeletonRow,
    Team,
    UserTeam,
    Plug,
    UnallocatedTable
  },
  props: {
    mode: {
      type: String,
      required: true,
      validator: function (v) {
        return ['allocated', 'unallocated'].includes(v);
      }
    }
  },
  data: () => ({
    groupsObserver: null,
    unallocatedObserver: null
  }),
  computed: {
    ...mapState('teamGroup', {
      //data
      groups: s => s.data.groups,
      unallocated: s => s.data.unallocated,
      peopleNearby: s => s.data.peopleNearby,
      filterList: s => s.data.filterList,
      canEdit: s => s.data.settings.canEdit,

      //meta
      isGroupLoading: s => s.meta.isGroupLoading,
      isLoadingMoreGroups: s => s.meta.isLoadingMoreGroups,
      isUnallocatedLoading: s => s.meta.isUnallocatedLoading,
      isLoadingMoreUnallocated: s => s.meta.isLoadingMoreUnallocated,
      loadedGroup: s => s.meta.loadedGroup,
      loadedUnallocated: s => s.meta.loadedUnallocated,
      loadedSetting: s => s.meta.loadedSetting,
      loadedPeopleNearby: s => s.meta.loadedPeopleNearby,

      groupsPage: s => s.meta.groupsPage,
      unallocatedPage: s => s.meta.unallocatedPage,
      hasMoreGroups: s => s.meta.hasMoreGroups,
      hasMoreUnallocated: s => s.meta.hasMoreUnallocated
    }),
    ...mapState('auth', {
      user: s => s.data.user
    }),
    isLoading() {
      if (this.canEdit) {
        return !this.loadedSetting || (!this.loadedGroup && !this.loadedUnallocated);
      } else {
        return (
          !this.loadedSetting ||
          !this.loadedGroup ||
          (!!this.groups.length && !this.loadedPeopleNearby)
        );
      }
    },
    showPlug() {
      // Если не идёт загрузка и все возможные массивы пусты
      return (
        !this.isLoading &&
        !this.groups.length &&
        !this.unallocated.length &&
        !this.peopleNearby.length
      );
    },
    plugText() {
      let title = '';
      let subtitle = '';

      if (!this.canEdit) {
        title = 'Дорогой участник!';
        subtitle =
          'На данный момент ты не состоишь в команде, так как ведется распределение. Вскоре мы присоединим тебя к одной из них!';
      } else {
        title = this.mode === 'allocated' ? 'Команды не найдены' : 'Пользователи не найдены';
        subtitle =
          this.mode === 'allocated'
            ? 'Попробуй изменить фильтр или создай новую команду'
            : 'Попробуй изменить фильтр';
      }

      return {
        title,
        subtitle
      };
    }
  },
  watch: {
    filterList: {
      handler: function (value) {
        this.setUrl(value);

        // Если идёт загрузка прав,
        // то по завершению загрузки
        // всё и так выполнится
        if (!this.loadedSetting) {
          return;
        }

        // Если обновился фильтр, то надо сносить старую инфу
        this.$store.dispatch('teamGroup/clearGroup');
        this.getGroups();
      },
      deep: true
    },
    mode() {
      this.$store.dispatch('teamGroup/clearGroup');
      this.getGroups();
    },
    isLoading(value) {
      if (!value) {
        this.$nextTick(() => {
          if (this.mode === 'allocated') {
            this.groupsObserver = new IntersectionObserver(([entry]) => {
              if (
                entry &&
                entry.isIntersecting &&
                this.hasMoreGroups &&
                !this.isLoadingMoreGroups
              ) {
                this.getTeamGroupData();
              }
            });
            const groupsContainer = this.$refs.groupsObserver;
            this.groupsObserver.observe(groupsContainer);
          }

          if (this.canEdit && this.mode === 'unallocated') {
            this.unallocatedObserver = new IntersectionObserver(([entry]) => {
              if (
                entry &&
                entry.isIntersecting &&
                this.hasMoreUnallocated &&
                !this.isLoadingMoreUnallocated
              ) {
                this.getUnallocated();
              }
            });
            const unallocatedContainer = this.$refs.unallocatedObserver;
            this.unallocatedObserver.observe(unallocatedContainer);
          }
        });
      }
    }
  },
  beforeMount() {
    this.initData();
  },
  beforeDestroy() {
    this.groupsObserver.disconnect();
    this.unallocatedObserver.disconnect();
  },
  methods: {
    ...mapActions('teamGroup', {
      getTeamGroupData: 'getTeamGroupData',
      getUnallocated: 'getUnallocated'
    }),
    setFilter(path, value) {
      this.$store.dispatch('teamGroup/setFilter', { path, value });
    },
    setUrl(queryList) {
      replaceMultipleRouteQuery(
        Object.entries(queryList).map(([name, value]) => ({
          value,
          name,
          method: 'replace'
        }))
      );
    },
    getUrlQuery() {
      Object.keys(this.filterList).forEach(key => {
        if (key in this.$route.query) {
          this.setFilter(key, this.$route.query[key]);
        }
      });
    },
    async getSetting() {
      await this.$store.dispatch('teamGroup/getTeamsSettings');
    },
    async getPeopleNearby() {
      await this.$store.dispatch('teamGroup/getPeopleNearby');
    },
    async initData() {
      await this.getSetting();

      if (this.canEdit) {
        this.getUrlQuery();
      }

      if (!this.isGroupLoading && !this.isUnallocatedLoading) {
        this.$store.commit('teamGroup/clearGroup');
        this.getGroups();
      }
    },
    async getGroups() {
      if (!this.loadedSetting) {
        await this.getSetting();
      }
      // Лучше грузить по очереди
      // но можно и вместе

      if (this.mode === 'allocated') {
        await this.getTeamGroupData();
      }

      // Если есть права на просмотр нераспределённых
      if (this.canEdit && this.mode === 'unallocated') {
        await this.getUnallocated();
      } else if (this.groups.length) {
        await this.getPeopleNearby();
      }
    }
  }
};
</script>

<style scoped lang="scss">
.intersection-observer {
  width: 1px;
  height: 30px;
  margin-top: -30px;
  background-color: transparent;
}
</style>
